import { ArrowLeft, BarChart3, Bookmark, Building2, ClipboardList, Mail, Megaphone, Search, ShieldCheck, Target, User as UserIcon, Users } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useMatch } from 'react-router-dom'
import { Logo } from '@alerte-immo/ui'
import { getSubscription } from '@/features/subscriptions/api'
import { UnpaidBanner } from '@/features/subscriptions/components/UnpaidBanner'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore, selectIsAdmin } from '@/features/auth/store'
import { getUnseenCount } from '@/features/notifications/api'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { TrialBriefGate } from '@/features/trial-brief/TrialBriefGate'

export function AppLayout() {
  const { t } = useTranslation()
  const isAdmin = useAuthStore(selectIsAdmin)

  // Préchauffe le tier d'abonnement pour toute l'app : la fiche bien sait alors
  // d'emblée si elle est verrouillée (pas de flash de contenu), et la modal de
  // quota recherches a son quota sous la main.
  const queryClient = useQueryClient()
  const { data: plan } = useQuery({
    queryKey: queryKeys.subscription,
    queryFn: getSubscription,
    // Plan impayé : la facture se règle dans le portail Stripe (autre onglet) ;
    // on relit le plan au retour sur l'app pour rétablir l'offre sans rechargement.
    refetchOnWindowFocus: (query) => (query.state.data?.status === 'unpaid' ? 'always' : false),
  })
  const unpaid = plan?.status === 'unpaid'

  // Facture réglée : fiches, filtres, quotas et résultats dépendent du palier
  // servi, on invalide tout pour retrouver l'offre rétablie.
  const wasUnpaid = useRef(unpaid)
  useEffect(() => {
    if (wasUnpaid.current && !unpaid) void queryClient.invalidateQueries()
    wasUnpaid.current = unpaid
  }, [unpaid, queryClient])

  // Badge « biens non vus » sur le nav Biens — endpoint COUNT léger, pollable.
  const { data: unseenCount = 0 } = useQuery({
    queryKey: queryKeys.unseenCount,
    queryFn: getUnseenCount,
    refetchInterval: 60_000,
  })

  const { pathname } = useLocation()

  // Trois contextes de nav : espace client (/app/*), espace admin (/admin/*),
  // et vision client (/admin/users/:id/app/* — menu client, liens réécrits
  // par l'ImpersonationShell pour rester dans l'impersonation).
  const impersonationMatch = useMatch('/admin/users/:userId/app/*')
  const impersonating = impersonationMatch != null
  const adminSpace = pathname.startsWith('/admin') && !impersonating
  // Base des liens client : préfixée en vision client pour rester dans l'impersonation.
  const base = impersonationMatch
    ? `/admin/users/${impersonationMatch.params.userId}/app`
    : '/app'

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-background sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <Link to="/app/biens">
            <Logo />
          </Link>
          <nav className="flex items-center gap-1">
            <div className="hidden md:flex items-center gap-1">
              {adminSpace ? (
                <>
                  <NavItem to="/admin/users" icon={<Users className="h-4 w-4" />} label="Utilisateurs" />
                  <NavItem to="/admin/prospects" icon={<Target className="h-4 w-4" />} label="Prospects" />
                  <NavItem to="/admin/campaigns" icon={<Megaphone className="h-4 w-4" />} label="Campagnes" />
                  <NavItem to="/admin/onboardings" icon={<ClipboardList className="h-4 w-4" />} label="Brief" />
                  <NavItem to="/admin/tier-settings" icon={<Mail className="h-4 w-4" />} label="Budget d’envoi" />
                  <NavItem to="/app/biens" icon={<ArrowLeft className="h-4 w-4" />} label="Espace client" />
                </>
              ) : (
                <>
                  <NavItem to={`${base}/biens`} icon={<Building2 className="h-4 w-4" />} label={t('nav.history')} badge={unseenCount} />
                  <NavItem to={`${base}/searches`} icon={<Search className="h-4 w-4" />} label={t('nav.searches')} />
                  <NavItem to={`${base}/lists`} icon={<Bookmark className="h-4 w-4" />} label={t('nav.lists')} />
                  <NavItem to={`${base}/stats`} icon={<BarChart3 className="h-4 w-4" />} label={t('nav.stats')} />
                  {isAdmin && !impersonating ? (
                    <NavItem to="/admin/users" icon={<ShieldCheck className="h-4 w-4" />} label={t('nav.admin')} />
                  ) : null}
                </>
              )}
            </div>
            <div className="md:ml-2 flex items-center gap-1">
              {!impersonating && <NotificationBell />}
              <ThemeToggle />
              {/* Pas de compte en vision client : gestion d'abonnement = écritures. */}
              {!impersonating && (
                <IconNavItem to="/app/account" icon={<UserIcon className="h-4 w-4" />} label={t('nav.account')} />
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* Portail = écriture, bloquée en vision client ; l'espace admin affiche le
          plan de l'admin, hors sujet ; /app/account porte le bandeau sur sa carte.
          Hors de <main> : les pages à marge négative (éditeur de zone) ne le
          recouvrent pas. */}
      {unpaid && !impersonating && !adminSpace && pathname !== '/app/account' ? (
        <div className="max-w-[1280px] w-full mx-auto px-4 md:px-6 pt-4">
          <UnpaidBanner />
        </div>
      ) : null}

      <main className="flex-1 max-w-[1280px] w-full mx-auto px-4 md:px-6 py-8 pb-24 md:pb-8">
        <Outlet />
      </main>
      {/* Le document défile (pas de conteneur interne). Clé par défaut
          (location.key) : haut de page à chaque navigation, position restaurée
          au retour arrière (POP). Les écritures de query string (filtres, tri,
          pagination) passent `preventScrollReset` pour ne pas remonter la page. */}
      <ScrollRestoration />

      {/* Mobile bottom navigation — thumb-first, mirrors the desktop nav. */}
      <nav
        aria-label="Navigation principale"
        className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-background pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex">
          {adminSpace ? (
            <>
              <BottomNavItem to="/admin/users" icon={<Users className="h-5 w-5" />} label="Utilisateurs" />
              <BottomNavItem to="/admin/prospects" icon={<Target className="h-5 w-5" />} label="Prospects" />
              <BottomNavItem to="/admin/campaigns" icon={<Megaphone className="h-5 w-5" />} label="Campagnes" />
              <BottomNavItem to="/admin/onboardings" icon={<ClipboardList className="h-5 w-5" />} label="Brief" />
              <BottomNavItem to="/admin/tier-settings" icon={<Mail className="h-5 w-5" />} label="Budget" />
              <BottomNavItem to="/app/biens" icon={<ArrowLeft className="h-5 w-5" />} label="Espace client" />
            </>
          ) : (
            <>
              <BottomNavItem to={`${base}/biens`} icon={<Building2 className="h-5 w-5" />} label={t('nav.history')} badge={unseenCount} />
              <BottomNavItem to={`${base}/searches`} icon={<Search className="h-5 w-5" />} label={t('nav.searches')} />
              <BottomNavItem to={`${base}/lists`} icon={<Bookmark className="h-5 w-5" />} label={t('nav.lists')} />
              <BottomNavItem to={`${base}/stats`} icon={<BarChart3 className="h-5 w-5" />} label={t('nav.stats')} />
              {isAdmin && !impersonating ? (
                <BottomNavItem to="/admin/users" icon={<ShieldCheck className="h-5 w-5" />} label={t('nav.admin')} />
              ) : null}
            </>
          )}
        </div>
      </nav>
      <TrialBriefGate disabled={impersonating || adminSpace} />
    </div>
  )
}

function BottomNavItem({ to, icon, label, badge = 0 }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `relative flex flex-1 flex-col items-center justify-center gap-1 h-14 text-[10px] font-medium transition-colors ${
          isActive ? 'text-primary' : 'text-muted'
        }`
      }
    >
      {badge > 0 ? (
        <span className="absolute right-1.5 top-0.5 grid h-[16px] min-w-[16px] place-items-center rounded-full border-2 border-background bg-primary px-1 font-mono text-[10px] font-semibold leading-none text-primary-foreground">
          {badge}
        </span>
      ) : null}
      {icon}
      {label}
    </NavLink>
  )
}

function NavItem({ to, icon, label, badge = 0 }: { to: string; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `relative inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? 'bg-surface text-foreground'
            : 'text-muted hover:text-foreground hover:bg-surface'
        }`
      }
    >
      {icon}
      <span>{label}</span>
      {badge > 0 ? (
        <span className="absolute -right-1.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-background bg-primary px-1.5 font-mono text-[11px] font-semibold text-primary-foreground">
          {badge}
        </span>
      ) : null}
    </NavLink>
  )
}

function IconNavItem({ to, icon, label }: { to: string; icon: React.ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      aria-label={label}
      className={({ isActive }) =>
        `inline-flex items-center justify-center h-9 w-9 rounded-md border border-border transition-colors ${
          isActive
            ? 'bg-surface text-foreground'
            : 'bg-surface text-muted hover:text-foreground'
        }`
      }
    >
      {icon}
    </NavLink>
  )
}
